/**
 * ProjectDetails Page Facade (100% Shared)
 *
 * Composes all necessary facades for the project details page.
 * Uses dependency injection pattern to remain platform-agnostic.
 *
 * This facade is 100% shared between web and plugin.
 * Platform-specific implementations (navigation, params) are injected.
 *
 * Pattern: Clean composition - delegates to smaller facades, no inline business logic.
 * Reference: useLandingPageFacade
 *
 * Usage (Web):
 * ```tsx
 * const navigation = useWebNavigation();
 * const auth = useAuthDialog();
 * const { id: projectId } = useParams();
 * const facade = useProjectDetailsPageFacade({
 *   navigation,
 *   auth,
 *   projectId,
 *   showToast
 * });
 * ```
 *
 * Usage (Plugin):
 * ```tsx
 * const navigation = usePluginNavigation();
 * const auth = usePairingCodeAuth();
 * const { currentParams } = usePluginNavigation();
 * const projectId = currentParams?.id;
 * const facade = useProjectDetailsPageFacade({
 *   navigation,
 *   auth,
 *   projectId,
 *   showToast
 * });
 * ```
 *
 * @module useProjectDetailsPageFacade
 * @category Facades/Pages
 */

import { useMemo, useState, useCallback, useEffect } from 'react';
import { useHeaderConfig } from '../../useHeaderConfig';
import { useConversationFacade } from '../shared/useConversationFacade';
import { useProjectDetailsFacade } from '../shared/useProjectDetailsFacade';
import { useSidebarConfig, type SidebarConfig, type ProjectMode } from '../shared/useSidebarConfig';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { selectCurrentProject, updateProjectMode } from '../../../store/slices/projectSlice';
import { selectConversationLlmFamily } from '../../../store/slices/conversationSlice';
import {
  selectPrototypeGenerationModel,
  setPrototypeGenerationModel,
} from '../../../store/slices/prototyperSlice';
import {
  selectPreviousPage,
  selectShowFirstProjectDialog,
  setShowFirstProjectDialog,
} from '../../../store/slices/appSlice';
import { getUserProfile } from '../../../store/slices/appSlice';
import type { NavigationFacade, ShowToastFn, AuthFacade } from '../shared/types';

export interface ProjectDetailsPageFacadeParams {
  /**
   * Platform-specific navigation implementation
   */
  navigation: NavigationFacade;

  /**
   * Platform-specific auth implementation
   */
  auth: AuthFacade;

  /**
   * Project ID from URL params or navigation context
   */
  projectId: string | undefined;

  /**
   * Platform-specific toast notification function
   */
  showToast: ShowToastFn;

  /**
   * Optional initial mode (e.g., from URL parameter for plugin redirects)
   */
  initialMode?: 'uncover' | 'prototyper';
}

export interface ProjectDetailsPageFacade {
  // ========== State ==========
  state: {
    isLoggedIn: boolean;
    currentMode: ProjectMode;
    llmFamily?: string;
    planDetails?: any;
  };

  // ========== Header ==========
  header: ReturnType<typeof useHeaderConfig>;

  // ========== Navigation ==========
  navigation: NavigationFacade;

  // ========== Conversation ==========
  conversation: ReturnType<typeof useConversationFacade>;

  // ========== Project Details ==========
  projectDetails: ReturnType<typeof useProjectDetailsFacade>;

  // ========== Auth ==========
  auth: AuthFacade;

  // ========== Sidebar ==========
  sidebar: SidebarConfig;

  // ========== Mode ==========
  mode: {
    current: ProjectMode;
    change: (mode: ProjectMode) => void;
  };

  // ========== Back Navigation ==========
  /**
   * Smart back handler that navigates to the correct page based on referrer
   */
  handleBack: () => void;

  // ========== First Project Dialog ==========
  /**
   * First project welcome dialog state and handlers
   */
  firstProjectDialog: {
    show: boolean;
    setShow: (show: boolean) => void;
    onClose: () => void;
    onStart: () => void;
  };

  // ========== Brand Sync ==========
  /**
   * Brand Sync feature configuration
   * Determines if Brand Sync is enabled based on user's plan
   */
  brandSync: {
    /** Whether Brand Sync is enabled for the user (paid feature) */
    isEnabled: boolean;
    /** Callback when user clicks Upgrade button */
    onUpgrade: () => void;
  };

  /** LLM for POST /prototype only (persisted in prototyper slice) */
  prototypeGeneration: {
    model: { modelProvider: string; modelChoice: string };
    setModel: (model: { modelProvider: string; modelChoice: string }) => void;
  };
}

/**
 * ProjectDetails Page Facade Hook
 *
 * Composes all necessary facades for the project details page.
 * 100% shared between web and plugin through dependency injection.
 */
export function useProjectDetailsPageFacade({
  navigation,
  auth,
  projectId,
  showToast,
  initialMode,
}: ProjectDetailsPageFacadeParams): ProjectDetailsPageFacade {
  const dispatch = useAppDispatch();

  // ========== Plan Details from Redux ==========
  const planDetails = useAppSelector((state) => state.app.planDetails);
  // Try to get accessToken from auth facade, fallback to empty string
  const accessToken = auth && (auth as any).accessToken ? (auth as any).accessToken : '';
  useEffect(() => {
    if (accessToken) {
      dispatch(getUserProfile(accessToken));
    }
  }, [accessToken, dispatch]);

  // ========== Select State ==========
  const llmFamily = useAppSelector(selectConversationLlmFamily);
  const prototypeGenerationModel = useAppSelector(selectPrototypeGenerationModel);
  const currentProject = useAppSelector((state) => selectCurrentProject(state, projectId || null));
  const previousPage = useAppSelector(selectPreviousPage);
  const showFirstProjectDialog = useAppSelector(selectShowFirstProjectDialog);

  // ========== Reusable Facades ==========
  const conversation = useConversationFacade();

  // ========== Mode State ==========
  // Initialize mode from URL parameter, project mode, or default to 'uncover'
  const currentProjectMode = currentProject?.mode;
  const [currentMode, setCurrentMode] = useState<ProjectMode>(() => {
    if (initialMode) return initialMode;
    if (currentProjectMode === 'prototyper') return 'prototyper';
    return 'uncover';
  });

  const projectDetails = useProjectDetailsFacade(
    projectId,
    navigation.toLanding,
    showToast,
    currentMode
  );

  // Sync mode with project changes (only if no initialMode from URL)
  useEffect(() => {
    if (!initialMode && projectDetails.currentProject?.mode) {
      setCurrentMode(projectDetails.currentProject.mode);
    }
  }, [projectDetails.currentProject?.mode, initialMode]);

  // Update project mode in database if initialMode is provided (e.g., from plugin redirect)
  useEffect(() => {
    if (initialMode && projectId && currentMode === initialMode) {
      dispatch(updateProjectMode({ projectId, mode: initialMode }));
    }
  }, [initialMode, projectId, currentMode, dispatch]);

  const handleModeChange = useCallback(
    (mode: ProjectMode) => {
      setCurrentMode(mode);
      // Update mode in Redux store and API
      if (projectId) {
        dispatch(updateProjectMode({ projectId, mode }));
      }
    },
    [projectId, dispatch]
  );

  // Back handler - navigate directly to previous page, skipping conversation routes
  const handleBack = useCallback(() => {
    // If we have a previousPage in Redux, navigate directly to it
    // This ensures we skip any conversation routes that might be in history
    if (previousPage === 'landing') {
      navigation.toLanding();
    } else if (previousPage === 'projects') {
      navigation.toProjectsList();
    } else {
      // Fallback to goBack if no previousPage stored
      navigation.toLanding();
    }
  }, [navigation, previousPage]);

  // First Project Dialog handlers
  const handleSetShowFirstProjectDialog = useCallback(
    (show: boolean) => {
      dispatch(setShowFirstProjectDialog(show));
    },
    [dispatch]
  );

  const handleFirstProjectDialogClose = useCallback(() => {
    dispatch(setShowFirstProjectDialog(false));
  }, [dispatch]);

  const handleFirstProjectDialogStart = useCallback(() => {
    dispatch(setShowFirstProjectDialog(false));
  }, [dispatch]);

  // ========== Header Config ==========
  const headerConfig = useHeaderConfig({
    onNavigateToProfile: navigation.toProfile,
    onNavigateToBilling: navigation.toBilling,
    onLoginClick: auth.openLogin,
    onRegisterClick: auth.openRegister,
    showToast,
    currentPath: `/projects/${projectId}`,
  });

  // ========== Sidebar Config ==========
  const sidebarConfig = useSidebarConfig({
    pageType: 'project',
    isLoggedIn: headerConfig.isLoggedIn,
    currentMode,
    hasProject: true,
    onModeChange: handleModeChange,
    onResetConversation: conversation.reset,
    onNavigateToLanding: navigation.toLanding,
  });

  // ========== Brand Sync Configuration ==========
  // Brand Sync is enabled only if the "Update Design System" feature has maxLimit "unlimited"
  const brandSync = useMemo(() => {
    const updateDesignSystemFeature = planDetails?.features?.find(
      (f: any) => f.name === 'Update Design System'
    );
    const isEnabled = updateDesignSystemFeature?.maxLimit === 'unlimited';
    return {
      isEnabled,
      onUpgrade: () => navigation.toBilling(),
    };
  }, [planDetails, navigation]);

  const setPrototypeGeneration = useCallback(
    (model: { modelProvider: string; modelChoice: string }) => {
      dispatch(setPrototypeGenerationModel(model));
    },
    [dispatch]
  );

  // ========== Return Composed Facade ==========
  return useMemo(
    () => ({
      state: {
        isLoggedIn: headerConfig.isLoggedIn,
        currentMode,
        llmFamily,
        planDetails,
      },
      header: headerConfig,
      navigation,
      conversation,
      projectDetails,
      auth,
      sidebar: sidebarConfig,
      mode: {
        current: currentMode,
        change: handleModeChange,
      },
      handleBack,
      firstProjectDialog: {
        show: showFirstProjectDialog,
        setShow: handleSetShowFirstProjectDialog,
        onClose: handleFirstProjectDialogClose,
        onStart: handleFirstProjectDialogStart,
      },
      brandSync,
      prototypeGeneration: {
        model: prototypeGenerationModel,
        setModel: setPrototypeGeneration,
      },
    }),
    [
      headerConfig,
      navigation,
      conversation,
      projectDetails,
      auth,
      sidebarConfig,
      currentMode,
      handleModeChange,
      llmFamily,
      handleBack,
      showFirstProjectDialog,
      handleSetShowFirstProjectDialog,
      handleFirstProjectDialogClose,
      handleFirstProjectDialogStart,
      planDetails,
      brandSync,
      prototypeGenerationModel,
      setPrototypeGeneration,
    ]
  );
}
